import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { EnvironmentService } from '@nomoh-monorepo/shared-data-access-environment';
import { IAceitarTermo, IPagerSettings, IResponse, ITermo, ITermoFiltro } from '@nomoh-monorepo/shared-types';
import { firstValueFrom } from 'rxjs';

export interface ITermosResponse extends IResponse<ITermo[]> {}

@Injectable({ providedIn: 'root' })
export class TermosDataService {
  private http = inject(HttpClient);
  private environment = inject(EnvironmentService);
  private baseUrl = `${this.environment.getApiUrl('nomoh')}/termo`;

  private _termos = signal<ITermo[]>([]);
  private _loading = signal<boolean>(false);
  private _erro = signal<string | null>(null);
  private _paginacao = signal<IPagerSettings>({
    total: 0,
    offSet: 0,
    limit: 20,
    param: 'dataPublicacao',
    ordenacao: -1,
  });
  private _filtros = signal<ITermoFiltro>({ sistemaId: null, tipo: null });

  $termos = this._termos.asReadonly();
  $loading = this._loading.asReadonly();
  $erro = this._erro.asReadonly();
  $paginacao = this._paginacao.asReadonly();
  $filtros = this._filtros.asReadonly();

  setLoading(loading: boolean) {
    this._loading.set(loading);
  }

  setErro(erro: string | null) {
    this._erro.set(erro);
  }

  setFiltros(filtros: Partial<ITermoFiltro>) {
    this._filtros.update((curr) => ({ ...curr, ...filtros }));
  }

  async carregarTermos(opcoes?: { offSet?: number; limit?: number }): Promise<void> {
    this.setLoading(true);
    this.setErro(null);
    try {
      const pag = this._paginacao();
      const filtroDto = {
        ...this._filtros(),
        offSet: opcoes?.offSet ?? pag.offSet ?? 0,
        limit: opcoes?.limit ?? pag.limit ?? 20,
        param: pag.param ?? 'dataPublicacao',
        ordenacao: pag.ordenacao ?? -1,
      };
      const response = await firstValueFrom(
        this.http.post<ITermosResponse>(`${this.baseUrl}/findAll`, filtroDto)
      );
      if (response.success) {
        this._termos.set(response.data);
        this._paginacao.set(response.pagerSettings);
      } else {
        this.setErro(response.errorMessage);
      }
    } catch (error) {
      this.setErro('Erro ao carregar termos');
      console.error(error);
    } finally {
      this.setLoading(false);
    }
  }

  async criarTermo(termo: Omit<ITermo, 'id' | 'ativo' | 'dataPublicacao' | 'dataInclusao' | 'dataAlteracao' | 'pessoaInclusao' | 'pessoaAlteracao'>): Promise<ITermo | null> {
    this.setLoading(true);
    this.setErro(null);
    try {
      const created = await firstValueFrom(
        this.http.post<ITermo>(`${this.baseUrl}/create`, termo)
      );
      await this.carregarTermos();
      return created;
    } catch (error) {
      this.setErro('Erro ao criar termo');
      console.error(error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  async buscarVigentes(sistemaId: string): Promise<ITermo[]> {
    return firstValueFrom(
      this.http.get<ITermo[]>(`${this.baseUrl}/vigente?sistema=${sistemaId}`)
    );
  }

  async registrarDecisao(dto: IAceitarTermo): Promise<void> {
    await firstValueFrom(
      this.http.post<void>(`${this.baseUrl}/aceitar`, dto)
    );
  }

  async buscar(filtros: Partial<ITermoFiltro>): Promise<void> {
    this.setFiltros(filtros);
    await this.carregarTermos({ offSet: 0 });
  }

  async limparFiltros(): Promise<void> {
    this._filtros.set({ sistemaId: null, tipo: null });
    await this.carregarTermos({ offSet: 0 });
  }
}
